import { IGetValueArgs } from "@components/smart/FieldInfo";
import BindingContext, { createPath } from "@odata/BindingContext";
import {
    PrEmploymentEntity,
    PrEmploymentTypeEntity,
    PrPayslipEmploymentEntity,
    PrPayslipEmploymentSalaryComponentEntity,
    PrPayslipEntity,
    PrSalaryComponentTemporalEntity
} from "@odata/GeneratedEntityTypes";
import { PrSalaryComponentTypeCode } from "@odata/GeneratedEnums";
import { IDefinition, IGetDefinition } from "@pages/PageUtils";
import { getEmployeeSelectDef } from "@pages/payroll/employee/Employee.utils";
import DelayedIncomeFormView, { IDelayedIncomeCustomData } from "@pages/payroll/payslip/delayedIncome/DelayedIncomeFormView";
import {
    areAllSalaryComponentsOfSameType,
    getSalaryComponentFieldDefinition
} from "@pages/payroll/salaryComponent/SalaryComponent.utils";
import i18next from "i18next";

import { BasicInputSizes, FastEntryInputSizes, FieldType } from "../../../../enums";
import { IFormDef } from "../../../../views/formView/Form";

export const EmploymentPath = BindingContext.localContext("Employment");

export const DELAYED_INCOME_FORM_ID = "DelayedIncomeSpecialForm";

export const getDefinition: IGetDefinition = (): IDefinition => {
    const SC_RegularSalaryTypeDef = getSalaryComponentFieldDefinition(PrPayslipEmploymentSalaryComponentEntity.RegularSalaryType);
    const {
        label,
        ...SalaryComponentPercentageDef
    } = getSalaryComponentFieldDefinition(PrSalaryComponentTemporalEntity.Percentage);
    const employeeDef = getEmployeeSelectDef();
    const form: IFormDef = {
        id: DELAYED_INCOME_FORM_ID,
        formControl: DelayedIncomeFormView,
        translationFiles: getDefinition.translationFiles,
        fieldDefinition: {
            [PrPayslipEntity.Employee]: {
                ...employeeDef,
                isReadOnly: false,
                fieldSettings: {
                    ...employeeDef.fieldSettings,
                    items: []
                }
            },
            [EmploymentPath]: {
                type: FieldType.ComboBox,
                width: BasicInputSizes.L,
                label: i18next.t("Payslip:DelayedIncome.Employment"),
                isRequired: true,
                fieldSettings: {
                    displayName: PrEmploymentEntity.NumberOurs,
                    shouldDisplayAdditionalColumns: true,
                    items: [],
                    itemsForRender: (_, args: IGetValueArgs) => {
                        const items = (args.storage.getCustomData() as IDelayedIncomeCustomData).employmentSelectItems;
                        const employeeId = args.storage.getValueByPath(PrPayslipEntity.Employee)?.Id;
                        if (employeeId) {
                            return items.filter(item => item.additionalData.Employee.Id === employeeId);
                        }
                        return items;
                    }
                },
                columns: [
                    { id: PrEmploymentEntity.NumberOurs },
                    { id: createPath(PrEmploymentEntity.Type, PrEmploymentTypeEntity.Name) }
                ],
            },
            // ---------- SalaryComponent fields ----------
            [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.SalaryComponents, PrPayslipEmploymentSalaryComponentEntity.Name)]: {
                ...getSalaryComponentFieldDefinition(PrPayslipEmploymentSalaryComponentEntity.Name),
            },
            [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.SalaryComponents, PrPayslipEmploymentSalaryComponentEntity.Type)]: {
                ...getSalaryComponentFieldDefinition(PrPayslipEmploymentSalaryComponentEntity.Type),
                type: FieldType.ComboBox,
                isReadOnly: false,
                isDisabled: false,
                defaultValue: PrSalaryComponentTypeCode.TimeBasedBonus,
                fieldSettings: {
                    ...getSalaryComponentFieldDefinition(PrPayslipEmploymentSalaryComponentEntity.Type).fieldSettings,
                    itemsForRender: (items, args: IGetValueArgs) => {
                        return items.filter(sc => sc.id !== PrSalaryComponentTypeCode.Regular);
                    }
                }
            },
            [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.SalaryComponents, PrPayslipEmploymentSalaryComponentEntity.RegularSalaryType)]: {
                ...SC_RegularSalaryTypeDef,
                isDisabled: true, // aways monthly for delayed income, so no need to enable the field
            },
            [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.SalaryComponents, PrPayslipEmploymentSalaryComponentEntity.IsBasicSalary)]: {
                ...getSalaryComponentFieldDefinition(PrPayslipEmploymentSalaryComponentEntity.IsBasicSalary)
            },
            [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.SalaryComponents, PrPayslipEmploymentSalaryComponentEntity.FixedBonusTaxImpact)]: {
                ...getSalaryComponentFieldDefinition(PrPayslipEmploymentSalaryComponentEntity.FixedBonusTaxImpact)
            },
            [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.SalaryComponents, PrPayslipEmploymentSalaryComponentEntity.FixedBonusPayOutImpact)]: {
                ...getSalaryComponentFieldDefinition(PrPayslipEmploymentSalaryComponentEntity.FixedBonusPayOutImpact),
            },
            [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.SalaryComponents, PrPayslipEmploymentSalaryComponentEntity.FixedBonusDeductionImpact)]: {
                ...getSalaryComponentFieldDefinition(PrPayslipEmploymentSalaryComponentEntity.FixedBonusDeductionImpact),
            },
            [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.SalaryComponents, PrPayslipEmploymentSalaryComponentEntity.PercentageOverride)]: {
                ...SalaryComponentPercentageDef,
                width: FastEntryInputSizes.XS
            },
            [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.SalaryComponents, PrPayslipEmploymentSalaryComponentEntity.AmountOverride)]: {
                label: i18next.t("Payslip:Form.AmountOverride"),
                ...getSalaryComponentFieldDefinition(PrPayslipEmploymentSalaryComponentEntity.Amount)
            },
            // ---------- /SalaryComponent fields ----------
        },
        groups: [
            {
                id: "Common",
                showGroupDivider: false,
                rows: [
                    [
                        { id: PrPayslipEntity.Employee },
                        { id: EmploymentPath }
                    ]
                ]
            },
            {
                id: "SalaryComponents",
                title: i18next.t("Payslip:DelayedIncome.SalaryComponents"),
                collection: PrPayslipEntity.Employments,
                lineItems: {
                    collection: PrPayslipEmploymentEntity.SalaryComponents,
                    canAdd: true,
                    canReorder: false,
                    isItemRemovable: true,
                    isItemCloneable: false,
                    isTheOnlyItemRemovable: false,
                    useLabelWrapping: areAllSalaryComponentsOfSameType,
                    order: null,
                    columns: [
                        { id: PrPayslipEmploymentSalaryComponentEntity.Name },
                        { id: PrPayslipEmploymentSalaryComponentEntity.Type },
                        { id: PrPayslipEmploymentSalaryComponentEntity.RegularSalaryType },
                        { id: PrPayslipEmploymentSalaryComponentEntity.FixedBonusTaxImpact },
                        { id: PrPayslipEmploymentSalaryComponentEntity.FixedBonusPayOutImpact },
                        { id: PrPayslipEmploymentSalaryComponentEntity.FixedBonusDeductionImpact },
                        { id: PrPayslipEmploymentSalaryComponentEntity.PercentageOverride },
                        { id: PrPayslipEmploymentSalaryComponentEntity.AmountOverride },
                    ],
                }
            }
        ]
    };

    return {
        form
    };
};

getDefinition.translationFiles = ["Payslip", "Common"];
